import { Flex, Text } from '@chakra-ui/react';
import { faBookSparkles } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Modal,
  Button,
  Input,
  FormFeedback,
  useToast
} from '@morressier/alembic';
import { FocusIssues } from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import { SubmitHandler, useForm } from 'react-hook-form';
import { createFocusIssue, updateFocusIssue } from 'src/api/mutations';
import { Field, FieldLabel } from 'src/components/Field';
import ListView from 'src/layouts/ListView';
import { z } from 'zod';
export type IFocusIssueFormData = {
  name: string;
  issueId: string;
  issueLink: string;
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  journalId: string;
  item?: FocusIssues;
}

const FocusIssueModal = ({ isOpen, onClose, journalId, item }: Props) => {
  const toast = useToast();
  const focusIssueValidator = z.object({
    name: z.string().trim().min(1, 'Special issue title is required'),
    issueId: z.string().trim().min(1, 'Issue ID is required'),
    issueLink: z
      .union([
        z.string().url({ message: 'Issue link should be valid' }),
        z.string().max(0)
      ])
      .optional()
  });

  const { register, handleSubmit, formState, setError } =
    useForm<IFocusIssueFormData>({
      defaultValues: {
        name: item?.name ?? '',
        issueId: item?.issueId ?? '',
        issueLink: item?.issueLink ?? ''
      },
      resolver: zodResolver(focusIssueValidator)
    });
  const focusIssueMutation = item
    ? updateFocusIssue(journalId, item.id)
    : createFocusIssue(journalId);
  const onSubmit: SubmitHandler<IFocusIssueFormData> = async data => {
    const { name, issueId, issueLink } = data;
    const status = item ? item.status : 'open';
    const response = await focusIssueMutation.mutateAsync({
      name,
      issueId,
      issueLink,
      status
    });
    if (response.isSuccess) {
      toast({
        variant: 'success',
        title: item
          ? `The special issue has been updated successfully`
          : `New special issue created successfully`
      });
      return onClose();
    }

    setError('name', {
      type: 'manual',
      message: response.message.error
    });
    setError('issueId', {
      type: 'manual',
      message: response.message.error
    });
  };
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width={{ base: 'calc(100vw - 16px)', md: '960px' } as any}
      closeButtonProps={{
        hidden: true
      }}
    >
      <ListView>
        <ListView.Title>
          <Flex flexDirection="column" gap={4} alignItems="flex-start">
            <FontAwesomeIcon icon={faBookSparkles} fontSize="42px" />
            <Text textStyle="headingGroup">
              {item ? 'Edit Special Issue' : 'Create Special Issue'}
            </Text>
          </Flex>
        </ListView.Title>

        <ListView.Body>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex flexDirection="column" gap={4}>
              <FieldLabel label="Special Issue Title" subText="" isRequired />
              <Input
                {...register('name')}
                isInvalid={Boolean(formState.errors.name)}
                placeholder="Enter the title of the Special Issue"
              />
              {formState.errors.name?.message ? (
                <FormFeedback variant="error">
                  {formState.errors.name.message}
                </FormFeedback>
              ) : null}
              <FieldLabel label="Issue ID" subText="" isRequired />
              <Input
                {...register('issueId')}
                isInvalid={Boolean(formState.errors.issueId)}
                placeholder="Enter the ID of the Special Issue"
              />
              {formState.errors.issueId?.message ? (
                <FormFeedback variant="error">
                  {formState.errors.issueId.message}
                </FormFeedback>
              ) : null}
              <Field label="Issue URL">
                <Input
                  {...register('issueLink')}
                  isInvalid={Boolean(formState.errors.issueLink)}
                  placeholder="Enter the scope link of the Special Issue"
                />
                {formState.errors.issueLink?.message ? (
                  <FormFeedback variant="error">
                    {formState.errors.issueLink.message}
                  </FormFeedback>
                ) : null}
              </Field>
              <Flex gap={2} justifyContent="flex-end" marginTop={3}>
                <Button variant="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary">
                  {item ? 'Save Changes' : 'Create Special Issue'}
                </Button>
              </Flex>
            </Flex>
          </form>
        </ListView.Body>
      </ListView>
    </Modal>
  );
};

export default FocusIssueModal;
